// The Makao table (.ai/makao.md): Kierki's table (hearts/HeartsTable.tsx, its classes and flights) with the stock and
// the pile in the middle. On a computer the classic table (the computer players' backs left / top / right, the hand
// fanned at the bottom), on a phone the compact one (the opponents as tiles, big cards in rows). A laid card flies
// from its player to the pile; a drawn one from the stock to its player. A jack or an ace asks its choice in a panel
// over the middle. The end of the deal opens the end window (the Kierki score window's look). Tap or click only; the
// root keeps the `.ht` class, so ui/tapClick.ts clicks its taps on the finger's lift (an iPad held them back).
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { t } from '../i18n';
import { QuickSay, SayBubble } from '../online/QuickSay';
import { SeatAvatar, seatFlag as seatFlagOf } from '../online/SeatAvatar';
import { flagged } from '../online/PlayerName';
import { backSrc, backSrcSet, faceSrc, faceSrcSet } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { useController } from '../store/gameStore';
import { isShortGame, reloadForNewGame } from '../hearts/nextGame';
import { AdSlot } from '../windows/AdSlot';
import { WindowFrame } from '../windows/WindowFrame';
import { useFitWindow } from '../windows/useFitWindow';
import { CARD_RATIO, Face, SHADOW, backPic, centre, flightShadow, ghost, usePhone, whenLanded } from '../hearts/HeartsTable';
import { CrownIcon, withSuits } from '../hearts/suits';
import { fixedNames } from '../hearts/names';
import { ASKABLE_RANKS, SUITS, canAdd, canDraw, cardKey, legalCards, mustWait, sameCard, winScore, type HCard, type MakaoState } from './rules';
import { DEAL_FLIGHT_MS, DEAL_STEP_MS, makaoStore, useMakao, type TableState } from './store';
import '../hearts/hearts.css';
import { SeatWait, TurnClock } from '../online/TurnClock';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import './makao.css';

/** A sentence with each real player's flag beside the name (owner 2026-11-01); not the hint over the hand, which has the
 *  suits only (owner 2026-11-02: "usuń stąd flagę użytkownika"). */
const flaggedText = (text: string) => flagged(text, makaoStore().getState().names);

const SYM = { C: '♣', D: '♦', S: '♠', H: '♥' } as const;
const rankName = (r: number): string => ({ 11: 'J', 12: 'Q', 13: 'K', 14: 'A' })[r] ?? String(r);
const FLY_MS = 360;
const CLOSE_MS = 450;
/** The pile shows every card laid, each a little turned, as laid by hand (owner 2026-10-27: with only the top 4 drawn,
 *  the lowest one vanished as each new card came); below this depth a card has no shadow (light on an old iPad). */
const PILE_FLAT = 6;

export const seatName = (seat: number): string => (seat === 0 ? t('hearts.you') : (makaoStore().getState().names ?? fixedNames(t)).name[seat]!);
/** Online: the seat's flag badge (a real player), else null (online/SeatAvatar.tsx). */
const seatFlag = (seat: number): string | null => seatFlagOf(makaoStore().getState().names, seat);

/** New game after a finished deal: a full reload, as the solitaires' Win window does (the ads refresh); after a deal
 *  under 2 minutes the next one at once (owner 2026-10-27, hearts/nextGame.ts). */
function useNewGame(): () => void {
  const ctrl = useController();
  const { startedAt, endedAt } = useMakao();
  return () => (isShortGame(startedAt, endedAt) ? void ctrl.newGame() : reloadForNewGame('makao'));
}

/** Where the player's card was when it was laid: its flight to the pile starts there. */
const laidFrom = new Map<string, DOMRect>();
const seatBox = (seat: number): DOMRect | null => document.querySelector(`.mk [data-src="${seat}"]`)?.getBoundingClientRect() ?? null;
const stockBox = (): DOMRect | null => document.querySelector('.mk .mk-stock')?.getBoundingClientRect() ?? null;
/** A card's small turn on the pile, the same for the same card. */
const tilt = (c: HCard): number => (((c.r * 7 + 'CDHS'.indexOf(c.s) * 5) % 11) - 5) * 1.4;

/** The player's hand: the cards that may be laid now are bright on the player's turn; the drawn card is lifted. */
function Hand({ g, deck, mine }: { g: MakaoState; deck: string; mine: boolean }) {
  const store = makaoStore();
  const legal = mine ? legalCards(g, 0) : [];
  const ref = useRef<HTMLDivElement>(null);
  const placed = useRef(new Map<string, { x: number; y: number }>());
  // a card left the hand or came in: the others slide to their new places (as in Kierki)
  useLayoutEffect(() => {
    const hand = ref.current;
    if (!hand) return;
    const now = new Map<string, { x: number; y: number }>();
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) now.set(el.dataset.card!, { x: el.offsetLeft, y: el.offsetTop });
    const before = placed.current;
    placed.current = now;
    if (!before.size || typeof hand.animate !== 'function') return;
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      const was = before.get(el.dataset.card!), is = now.get(el.dataset.card!)!;
      if (!was || (Math.abs(was.x - is.x) < 0.5 && Math.abs(was.y - is.y) < 0.5)) continue;
      el.animate([{ transform: `translate(${was.x - is.x}px, ${was.y - is.y}px)` }, { transform: 'none' }], { duration: CLOSE_MS, easing: 'ease-in-out' });
    }
  });
  const hand = g.hands[0]!;
  return (
    <div ref={ref} className="ht-hand" data-count={hand.length} style={{ '--n': hand.length } as CSSProperties}>
      {hand.map((c) => {
        const can = legal.some((x) => sameCard(x, c));
        return (
          <button
            key={cardKey(c)}
            type="button"
            className="ht-card"
            data-card={cardKey(c)}
            data-off={mine && !can ? '' : undefined}
            data-drawn={g.drawn && sameCard(g.drawn, c) ? '' : undefined}
            onClick={(e) => {
              if (!can) return;
              laidFrom.set(cardKey(c), e.currentTarget.getBoundingClientRect());
              store.lay(c);
            }}
          >
            <span className="ht-sh" aria-hidden="true" />
            <Face card={c} deck={deck} />
          </button>
        );
      })}
    </div>
  );
}

/** A card of the pile; the newest flies in from the one who laid it (FLIP: drawn at its place, animated from there). */
function PileCard({ c, depth, deck, fly }: { c: HCard; depth: number; deck: string; fly: boolean }) {
  const deep = depth >= PILE_FLAT;
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!fly || !el || typeof el.animate !== 'function') return;
    const to = el.getBoundingClientRect();
    const st = makaoStore().getState();
    const mine = laidFrom.get(cardKey(c));
    laidFrom.delete(cardKey(c));
    const from = mine ?? seatBox(st.game?.turn ?? st.last?.seat ?? 0);
    if (!from || !to.width) return;
    const a = centre(from), b = centre(to);
    const scale = mine ? mine.width / to.width : 0.6;
    el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${scale}) rotate(0deg)` }, { transform: `rotate(${tilt(c)}deg)` }], { duration: FLY_MS, easing: 'cubic-bezier(.2,.75,.3,1)' });
    flightShadow(el, SHADOW.arc, { duration: FLY_MS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, when the card arrives
  return (
    <div ref={ref} className="mk-pile__card" data-card={cardKey(c)} data-depth={depth} data-deep={deep || undefined} style={{ transform: `rotate(${tilt(c)}deg)` }}>
      {deep ? null : <span className="ht-sh" aria-hidden="true" />}
      <Face card={c} deck={deck} />
    </div>
  );
}

function Pile({ g, deck, dealing }: { g: MakaoState; deck: string; dealing: boolean }) {
  const n = g.pile.length;
  return (
    <div className="mk-pile" data-pile="">
      {g.pile.map((c, i) => (
        // keyed by the card and its place in the pile: a card laid again after a reshuffle is a new one
        <PileCard key={`${cardKey(c)}-${i}`} c={c} depth={n - 1 - i} deck={deck} fly={!dealing && i === n - 1 && n > 1} />
      ))}
    </div>
  );
}

/** The stock: a few backs, its count; the player's tap draws (or takes the open attack). */
function Stock({ g, back, mine }: { g: MakaoState; back: string; mine: boolean }) {
  const can = mine && canDraw(g, 0);
  const layers = Math.min(4, Math.max(1, Math.ceil(g.stock.length / 8)));
  return (
    <button type="button" className="mk-stock" data-can={can || undefined} disabled={!can} aria-label={t('makao.stock')} onClick={() => makaoStore().draw()}>
      {g.stock.length
        ? Array.from({ length: layers }, (_, k) => <img key={k} className="mk-stock__back" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} style={{ '--k': k } as CSSProperties} />)
        : <span className="mk-stock__empty" />}
      <span className="mk-stock__count">{g.stock.length}</span>
    </button>
  );
}

/** The open attack and the open demand, as badges by the pile. */
function Status({ g }: { g: MakaoState }) {
  const items: string[] = [];
  if (g.attack?.kind === 'draw') items.push(t('makao.state.draw', { n: g.attack.count }));
  if (g.attack?.kind === 'wait') items.push(t('makao.state.wait', { n: g.attack.count }));
  if (g.demand?.kind === 'rank') items.push(t('makao.state.rank', { rank: rankName(g.demand.rank) }));
  if (g.demand?.kind === 'suit') items.push(t('makao.state.suit', { suit: SYM[g.demand.suit] }));
  return (
    <div className="mk-status" data-empty={items.length ? undefined : ''}>
      {items.map((s) => (
        <span key={s} className="mk-badge">
          {withSuits(s)}
        </span>
      ))}
    </div>
  );
}

function hint(g: MakaoState, st: TableState): string {
  if (st.dealing || g.phase !== 'play') return '';
  const did = st.last && st.last.seat !== 0 && st.last.seat !== g.turn
    ? st.last.what === 'take' ? t('makao.did.take', { name: seatName(st.last.seat), n: st.last.n })
      : st.last.what === 'draw' || st.last.what === 'keep' ? t('makao.did.draw', { name: seatName(st.last.seat) })
        : st.last.what === 'wait' ? t('makao.did.wait', { name: seatName(st.last.seat) }) : ''
    : '';
  if (g.turn !== 0) {
    const turn = mustWait(g, g.turn) ? t('makao.did.wait', { name: seatName(g.turn) }) : t('makao.hint.turn', { name: seatName(g.turn) });
    return did ? `${did} · ${turn}` : turn;
  }
  if (st.choosing === 'rank') return t('makao.hint.rank');
  if (st.choosing === 'suit') return t('makao.hint.suit');
  if (mustWait(g, 0)) return t('makao.hint.wait');
  if (g.drawn) return t('makao.hint.drawn');
  if (g.laid.length) return t('makao.hint.more');
  const any = legalCards(g, 0).length > 0;
  if (g.attack?.kind === 'draw') return t(any ? 'makao.hint.attack' : 'makao.hint.attackNo', { n: g.attack.count });
  if (g.attack?.kind === 'wait') return any ? t('makao.hint.fours') : t('makao.hint.wait');
  if (g.demand?.kind === 'rank') return t('makao.hint.askedRank', { rank: rankName(g.demand.rank) });
  if (g.demand?.kind === 'suit') return t('makao.hint.askedSuit', { suit: SYM[g.demand.suit] });
  return t(any ? 'makao.hint.play' : 'makao.hint.noCard');
}

/** The buttons by the hint: draw / take / wait, end the turn, keep the drawn card, a new game after the end. */
function Actions({ g, st }: { g: MakaoState; st: TableState }) {
  const store = makaoStore();
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (g.phase === 'over') {
    // online the end window has Play again / Leave; a new game here would reload the page
    if (online || uiStore().isOpen('makao_end')) return null;
    return (
      <button type="button" className="ht-go" id="makao_again" onClick={newGame}>
        {t('hearts.score.newGame')}
      </button>
    );
  }
  if (st.dealing || g.turn !== 0 || st.choosing || mustWait(g, 0)) return null;
  if (g.drawn)
    return (
      <button type="button" className="ht-go mk-go--soft" id="makao_keep" onClick={() => store.pass()}>
        {t('makao.button.keep')}
      </button>
    );
  if (g.laid.length)
    return canAdd(g) ? (
      <button type="button" className="ht-go" id="makao_done" onClick={() => store.finish()}>
        {t('makao.button.done')}
      </button>
    ) : null;
  const label = g.attack?.kind === 'draw' ? t('makao.button.take', { n: g.attack.count }) : g.attack?.kind === 'wait' ? t('makao.button.wait') : t('makao.button.draw');
  return (
    <button type="button" className="ht-go mk-go--soft" id="makao_draw" onClick={() => store.draw()}>
      {label}
    </button>
  );
}

/** The jack's rank or the ace's suit, in a panel over the middle. */
function Picker({ choosing }: { choosing: 'rank' | 'suit' | null }) {
  if (!choosing) return null;
  const store = makaoStore();
  return (
    <div className="mk-pick" role="dialog" aria-label={t(choosing === 'rank' ? 'makao.hint.rank' : 'makao.hint.suit')}>
      <b>{t(choosing === 'rank' ? 'makao.hint.rank' : 'makao.hint.suit')}</b>
      <div className="mk-pick__row">
        {choosing === 'rank'
          ? [
              ...ASKABLE_RANKS.map((r) => (
                <button key={r} type="button" className="mk-pick__btn" data-rank={r} onClick={() => store.choose({ rank: r })}>
                  {r}
                </button>
              )),
              <button key="none" type="button" className="mk-pick__btn mk-pick__btn--wide" data-rank="none" onClick={() => store.choose({ rank: null })}>
                {t('makao.button.nothing')}
              </button>,
            ]
          : SUITS.map((s) => (
              <button key={s} type="button" className="mk-pick__btn mk-pick__suit" data-suit={s} onClick={() => store.choose({ suit: s })}>
                {SYM[s]}
              </button>
            ))}
      </div>
    </div>
  );
}

/** "Makao!" over a seat that has one card left. */
const Said = ({ st, seat }: { st: TableState; seat: number }) =>
  st.said?.seat === seat ? (
    <i key={st.said.n} className="mk-said">
      {t('makao.said')}
    </i>
  ) : null;

const turnOf = (g: MakaoState, seat: number) => g.phase === 'play' && g.turn === seat;

function Plate({ g, st, seat, src }: { g: MakaoState; st: TableState; seat: number; src?: boolean }) {
  return (
    <div className="ht-plate" data-seat={seat} data-turn={turnOf(g, seat) || undefined} data-src={src ? seat : undefined} data-wait={g.waits[seat]! > 0 || undefined}>
      <SeatAvatar name={seatName(seat)} flag={seatFlag(seat)} />
      <SeatWait seat={seat} />
      <div className="ht-plate__text">
        <b>{seatName(seat)}</b>
        <span>{t('makao.cards', { n: g.hands[seat]!.length })}</span>
      </div>
      <Said st={st} seat={seat} />
    </div>
  );
}

function Backs({ g, seat, back }: { g: MakaoState; seat: number; back: string }) {
  const n = g.hands[seat]!.length;
  return (
    <div className="ht-backs" data-src={seat} aria-hidden="true" style={{ '--n': n } as CSSProperties}>
      {g.hands[seat]!.map((_, i) => (
        <img key={i} className="ht-back" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
      ))}
    </div>
  );
}

function Middle({ g, st, deck, back, mine }: { g: MakaoState; st: TableState; deck: string; back: string; mine: boolean }) {
  return (
    <div className="mk-field">
      <Stock g={g} back={back} mine={mine} />
      <Pile g={g} deck={deck} dealing={st.dealing} />
      <Status g={g} />
      <Picker choosing={st.choosing} />
    </div>
  );
}

/** A: the classic table (a computer). */
function ClassicTable({ g, st, deck, back }: { g: MakaoState; st: TableState; deck: string; back: string }) {
  const mine = turnOf(g, 0) && !st.dealing;
  return (
    <div className="ht-a">
      <div className="ht-seat ht-seat--top">
        <Plate g={g} st={st} seat={2} />
        <Backs g={g} seat={2} back={back} />
      </div>
      <div className="ht-seat ht-seat--left">
        <Plate g={g} st={st} seat={1} />
        <Backs g={g} seat={1} back={back} />
      </div>
      <div className="ht-seat ht-seat--right">
        <Plate g={g} st={st} seat={3} />
        <Backs g={g} seat={3} back={back} />
      </div>
      <div className="ht-mid">
        <div className="ht-felt" aria-hidden="true" />
        <Middle g={g} st={st} deck={deck} back={back} mine={mine} />
      </div>
      <div className="ht-hintrow">
        <span className="ht-hint" data-hint="">
          {withSuits(hint(g, st))}
          <TurnClock at={st.deadlineAt} />
        </span>
        <Actions g={g} st={st} />
      </div>
      <div className="ht-me">
        <Plate g={g} st={st} seat={0} src />
      </div>
      <Hand g={g} deck={deck} mine={mine} />
    </div>
  );
}

/** C: compact, big cards (a phone). */
function CompactTable({ g, st, deck, back }: { g: MakaoState; st: TableState; deck: string; back: string }) {
  const mine = turnOf(g, 0) && !st.dealing;
  return (
    <div className="ht-c">
      <div className="ht-chips">
        {[1, 2, 3].map((p) => (
          <div key={p} className="ht-chip" data-seat={p} data-turn={turnOf(g, p) || undefined} data-src={p} data-wait={g.waits[p]! > 0 || undefined}>
            <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
            <SeatWait seat={p} />
            <b>{seatName(p)}</b>
            <span>{t('makao.cards', { n: g.hands[p]!.length })}</span>
            <Said st={st} seat={p} />
          </div>
        ))}
      </div>
      <div className="mk-c-mid">
        <Middle g={g} st={st} deck={deck} back={back} mine={mine} />
      </div>
      {/* a slot of two lines: a longer or shorter hint no longer moves the table above (owner 2026-11-02) */}
      <div className="ht-msgslot">
        <div className="ht-msg">
          <span className="ht-hint" data-hint="">
            {withSuits(hint(g, st))}
            <TurnClock at={st.deadlineAt} />
          </span>
          <Actions g={g} st={st} />
        </div>
      </div>
      <div className="ht-mechip" data-seat={0} data-turn={turnOf(g, 0) || undefined} data-src={0}>
        {seatName(0)} · {t('makao.cards', { n: g.hands[0]!.length })}
        <Said st={st} seat={0} />
      </div>
      <Hand g={g} deck={deck} mine={mine} />
    </div>
  );
}

/** The deal: from the stock in the middle, one card to each player round the table (the left one first), five times;
 *  then the first card of the pile turns up. The player's cards fly themselves; the computer players' backs as copies
 *  above the table (a real back flew under the field, as in Kierki). Taps wait (data-dealing). */
function useDealAnimation(deal: number, back: string): void {
  useLayoutEffect(() => {
    const root = document.querySelector<HTMLElement>('.mk');
    const from = stockBox();
    if (!deal || !root || !from || typeof root.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    const flights: Animation[] = [];
    const order = (seat: number, i: number) => i * 4 + ((seat + 3) % 4);
    const top = centre(from);
    document.querySelectorAll<HTMLElement>('.mk .ht-hand .ht-card').forEach((el, i) => {
      const to = el.getBoundingClientRect();
      const b = centre(to);
      const timing = { duration: DEAL_FLIGHT_MS, delay: order(0, i) * DEAL_STEP_MS };
      flights.push(el.animate([{ transform: `translate(${top.x - b.x}px, ${top.y - b.y}px) scale(${from.width / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' }));
      flightShadow(el, SHADOW.arc, timing);
    });
    for (const seat of [1, 2, 3]) {
      const backs = [...document.querySelectorAll<HTMLElement>(`.mk .ht-backs[data-src="${seat}"] .ht-back`)];
      const targets = backs.length ? backs.map((b) => b.getBoundingClientRect()) : [];
      const tile = backs.length ? null : document.querySelector(`.mk [data-src="${seat}"]`)?.getBoundingClientRect();
      for (let i = 0; i < 5; i++) {
        const to = targets[i] ?? (tile ? new DOMRect(tile.left + tile.width / 2 - 20, tile.top + tile.height / 2 - 26, 40, 53) : null);
        if (!to) continue;
        const c = centre(to);
        const w = from.width, h = w * CARD_RATIO;
        const g = ghost(new DOMRect(c.x - w / 2, c.y - h / 2, w, h), backPic(back), ghosts);
        const timing = { duration: DEAL_FLIGHT_MS, delay: order(seat, i) * DEAL_STEP_MS };
        const f = g.animate([{ transform: `translate(${top.x - c.x}px, ${top.y - c.y}px)`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: `scale(${to.width / w})`, opacity: backs.length ? 1 : 0 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' });
        flightShadow(g, SHADOW.arc, timing);
        flights.push(f);
        void f.finished.then(() => g.remove(), () => g.remove());
        if (backs[i]) flights.push(backs[i]!.animate([{ opacity: 0 }, { opacity: 0 }], { duration: timing.delay + DEAL_FLIGHT_MS }));
      }
    }
    // the first card of the pile turns up off the stock after the last card is dealt
    const first = document.querySelector<HTMLElement>('.mk .mk-pile__card');
    if (first) {
      const to = first.getBoundingClientRect(), b = centre(to);
      const delay = 20 * DEAL_STEP_MS + 120;
      flights.push(first.animate([{ transform: `translate(${top.x - b.x}px, ${top.y - b.y}px) scale(0, 1)`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: first.style.transform || 'none', opacity: 1 }], { duration: DEAL_FLIGHT_MS, delay, easing: 'ease-out', fill: 'backwards' }));
    }
    root.dataset.dealing = '';
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      delete root.dataset.dealing;
    };
    const cancel = whenLanded(flights, 20 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 1500, end);
    return () => {
      cancel();
      end();
      for (const g of ghosts) g.remove();
    };
  }, [deal]); // eslint-disable-line react-hooks/exhaustive-deps -- one per deal
}

/** Cards drawn off the stock fly to their player: the player's new cards themselves (turning face up on the way), a
 *  computer player's as back copies to its seat, one after the other. */
function useDrawFlights(g: MakaoState | null, dealing: boolean, back: string): void {
  const seen = useRef<{ counts: number[]; mine: Set<string> } | null>(null);
  useLayoutEffect(() => {
    if (!g) return;
    const counts = g.hands.map((h) => h.length);
    const mine = new Set(g.hands[0]!.map(cardKey));
    const before = seen.current;
    seen.current = { counts, mine };
    const from = stockBox();
    if (dealing || !before || !from || typeof document.body.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    const a = centre(from);
    let k = 0;
    for (const el of document.querySelectorAll<HTMLElement>('.mk .ht-hand .ht-card')) {
      if (before.mine.has(el.dataset.card!)) continue;
      const to = el.getBoundingClientRect(), b = centre(to);
      const timing = { duration: FLY_MS + 80, delay: k++ * 110 };
      el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${from.width / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'backwards' });
      flightShadow(el, SHADOW.arc, timing);
    }
    for (const seat of [1, 2, 3]) {
      const more = counts[seat]! - before.counts[seat]!;
      const to = more > 0 ? seatBox(seat) : null;
      if (!to) continue;
      const c = centre(to), w = from.width, h = w * CARD_RATIO;
      for (let i = 0; i < Math.min(more, 8); i++) {
        const ghostEl = ghost(new DOMRect(c.x - w / 2, c.y - h / 2, w, h), backPic(back), ghosts);
        const timing = { duration: FLY_MS + 80, delay: i * 110 };
        const f = ghostEl.animate([{ transform: `translate(${a.x - c.x}px, ${a.y - c.y}px)`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'scale(0.55)', opacity: 0 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'backwards' });
        flightShadow(ghostEl, SHADOW.arc, timing);
        void f.finished.then(() => ghostEl.remove(), () => ghostEl.remove());
      }
    }
  });
}

export function MakaoTable() {
  const st = useMakao();
  const g = st.game;
  const skin = useUiState().options.skin;
  const phone = usePhone();
  useDealAnimation(st.deal, skin.cardBackType);
  useDrawFlights(g, st.dealing, skin.cardBackType);
  return (
    <div id="main" className="board ht mk" data-board="" data-game="makao" data-layout={phone ? 'compact' : 'classic'} data-phase={g?.phase} data-deck={skin.deckType} data-choosing={st.choosing ?? undefined}>
      {g ? phone ? <CompactTable g={g} st={st} deck={skin.deckType} back={skin.cardBackType} /> : <ClassicTable g={g} st={st} deck={skin.deckType} back={skin.cardBackType} /> : null}
    </div>
  );
}

/** The hero of the end window: the special cards, fanned (as the Kierki window's). */
const HERO = ['2h', '3d', 'Jc', 'Qh', 'As'];
const FIT_STEPS = 6;

/** The end of the deal: who went out, the cards left in every hand, the player's score when won. */
export function MakaoEndWindow({ instance }: { instance: WindowInstance }) {
  const st = useMakao();
  const g = st.game;
  const skin = useUiState().options.skin;
  useFitWindow('.lbx_window.lbx_makao_end', FIT_STEPS, g ? `${g.winner}` : null);
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (!g) return null;
  const youWin = g.winner === 0;
  return (
    <WindowFrame name="makao_end" header={t('makao.end.title')} overlay closing={instance.closing}>
      <div className="tab_contents">
        <div className="hs-backdrop" aria-hidden="true" style={{ '--hs-back': `url("${backSrc(skin.cardBackType)}")` } as CSSProperties} />
        <div className="hs-cont">
          <div className="hs-hero" aria-hidden="true">
            {HERO.map((id, i) => (
              <img key={id} className="hs-hero__card" src={faceSrc(skin.deckType, id)} srcSet={faceSrcSet(skin.deckType, id)} alt="" draggable={false} style={{ '--i': i - 2 } as CSSProperties} />
            ))}
          </div>
          <div className="hs-title" data-win={youWin || undefined}>
            {flaggedText(youWin ? t('makao.end.win') : t('makao.end.lose', { name: seatName(g.winner) }))}
          </div>
          <div className="hs-sub">{youWin && !online ? t('makao.end.score', { n: winScore(g) }) : t('makao.end.left')}</div>
          <div className="hs-tiles">
            {[0, 1, 2, 3].map((p) => (
              <div key={p} className="hs-tile" data-me={p === 0 || undefined} data-best={p === g.winner || undefined}>
                <SayBubble seat={p} />
                <div className="hs-tile__who">
                  <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
                  <b>{seatName(p)}</b>
                  {p === g.winner ? <CrownIcon /> : null}
                </div>
                <div className="hs-tile__total">{g.hands[p]!.length}</div>
                <small>{t('makao.cards', { n: g.hands[p]!.length })}</small>
              </div>
            ))}
          </div>
          {online ? <QuickSay iWon={youWin} /> : null}
        </div>
        <AdSlot slot={2} />
        <div className="rules_buttons hs-buttons">
          {online ? (
            // online: the same table again (Start) or leave it
            <>
              <input type="button" className="mp_btn_secondary" id="mp_cards_leave" value={t('lobby.room.leave')} onClick={() => mpStore().leaveCards()} />
              <input type="button" className="nomoves_shuffle" id="mp_cards_again" value={t('lobby.again')} onClick={() => mpStore().again()} />
            </>
          ) : (
            <input type="button" className="nomoves_shuffle" id="makao_new" value={t('hearts.score.newGame')} onClick={newGame} />
          )}
        </div>
      </div>
    </WindowFrame>
  );
}

